<template>
  <main class="main skin">
    <PagesItem
      v-if="item"
      v-model:item="item"
      :loading="loading"
    />
  </main>
</template>

<script lang="ts" setup>
import type { ItemPageInfo } from '~/graphql/generated/schema-types';
import { useAsyncData } from 'nuxt/app';
import { computed } from 'vue';
import { useAlternateLinks } from '~/composables/alternate-links';
import { EventBus } from '~/composables/bus';
import { useDefineProduct } from '~/composables/define-product';
import { getItemGeneral } from '~/utils/api';
import { getImageFromWaxpeer } from '~/utils/get-image-url';
import { getItemLinkWithoutExtAndStatrack } from '~/utils/name-utils';

const route = useRoute();
const item = ref<ItemPageInfo | null>(null);
const { locale, t } = useI18n();
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;
const { defineUseProduct } = useDefineProduct();
const loading = ref(false);

function getLinkWithoutExterior(name: string) {
  return name.replace(/-(factory-new|minimal-wear|field-tested|well-worn|battle-scarred)/, '');
}

async function getItemFetch() {
  const route = useRoute();
  const itemName = route.params.name;
  const exterior = route.params.exterior || '';
  const fullName = `${itemName}${exterior ? `-${exterior}` : ''}`;
  try {
    const { data, errors } = await getItemGeneral({ name: fullName });

    if (errors?.length) {
      console.log(`data: ${data}, IItem page fetch error: ${JSON.stringify(errors)}, method: getItem({name: fullName}), fullName = ${fullName}`);
      throw new Error(errors[0].message);
    }

    // const {data: data2, errors: errors2} = await getItemRating(data.itemPageInfo.id)
    //
    // if (errors2?.length) {
    //   EventBus?.$emit('notify', {
    //     success: false,
    //     msg: errors2[0].message || 'An error occurred'
    //   })
    //   return {...data.itemPageInfo}
    // }

    return {
      ...data.itemPageInfo,
      // , ...data2.getItemRating
    };
  } catch (e: any) {
    console.log('Item page fetch error: ', e.message);
    EventBus?.$emit('notify', {
      success: false,
      msg: e.message || 'An error occurred',
    });
    throw new Error(e.message || 'An error occurred');
  }
}

const {
  error,
  data,
} = await useAsyncData(`getItem${route.params.name}`, getItemFetch, { watch: [route] });

if (error.value) {
  if (process.client) {
    window.location.reload();
  }

  console.log(`Item page fetch error: ${error.value?.message}`);
  throw createError({
    status: 404,
    statusText: `Item page fetch error: ${error.value?.message}`,
  });
} else {
  item.value = (data.value || {}) as ItemPageInfo;

  if (data.value?.category === 'Collections') {
    await navigateTo(`/collections` + `/${route.params.name}`, { redirectCode: 301 });
  }
}

const itemTitle = computed(() => {
  let phase = '';
  if (item.value?.phase) {
    phase = ` ${t(`phases.${item.value?.phase}`)}`;
  }
  let itemName = ((item.value?.locals?.localed_name ? item.value?.locals?.localed_name + (phase || '') : '')
    || item.value?.market_hash_name || '');

  itemName = getLinkWithoutExterior(item.value?.link || '') === item.value?.link ? itemName : itemName.replace(/\s*\(.*?\)$/, '');

  return t('heads.item.title', { name: itemName });
});

const itemDescription = computed(() => {
  const name = (item.value?.locals?.localed_name || item.value?.market_hash_name || '').replace(/\s*\(.*?\)$/, '');
  if (item.value?.rarity_color === '#eb4b4b') {
    return t('heads.item.desc_covert', { name });
  }
  if (item.value?.rarity_color === '#d32ce6') {
    return t('heads.item.desc_classified', { name });
  }
  if (item.value?.rarity_color === '#8847ff') {
    return t('heads.item.desc_consumer', { name });
  }
  if (item.value?.rarity_color === '#4b69ff') {
    return t('heads.item.desc_industrial', { name });
  }
  return t('heads.item.desc_default', { name });
  // return item.value?.locals?.description || t('heads.item.description', {name: item.value?.market_hash_name || ''})
});

function getItemNameWithCategoryLocal(name: string) {
  if (!name)
    return '';

  return name
    .replace(/\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)\s*$/i, '') // убираем только состояние
    .replace(/\s*\|\s*/g, '-') // заменяем разделитель
    .replace(/\s+/g, '-') // пробелы — в тире
    .toLowerCase();
}

const modifiedNames: Record<string, string> = {
  'ump-45-k-o-factory': 'ump-45-ko-factory',
};
const modifiedName = computed(() => {
  const name = getModifiedString(getItemNameWithCategoryLocal(item.value?.market_hash_name?.replace(/Souvenir|StatTrak/g, '').replace(/Doppler.*/, 'doppler') || ''));
  const key = Object.keys(modifiedNames).find(key => name.includes(key));
  return key ? name.replace(key, modifiedNames[key]) : name;
});

const imageUrl = computed(() => {
  if (item.value?.min_float !== null && item.value?.min_float !== undefined && item.value?.max_float) {
    return getImageFromProxy(`https://images.csmarketcap.com/exterior-viewer/${modifiedName.value}/${+(+(item.value?.min_float || '0') * 100).toFixed() || 1}.webp`, '1200:600');
  }
  return getImageFromWaxpeer(item.value?.market_hash_name || '', '1200:600');
});

defineOgImageComponent('Dynamic', {
  imageUrl: getImageFromProxy(imageUrl.value || '', '1200:600').replace('f:webp', 'f:png'),
});

const { alternateLinks, canonical } = useAlternateLinks(getItemLinkWithoutExtAndStatrack({
  name: item.value?.market_hash_name,
  slug: item.value?.link,
  usedLocalePath: false,
}));

alternateLinks.value = alternateLinks.value.map((link) => {
  if (link.rel !== 'canonical' || !item?.value?.volume?.[0]?.full_name) {
    return link;
  }

  return {
    ...link,
    href: `${siteHost}${getItemLinkWithoutExtAndStatrack({
      name: item?.value?.volume?.[0]?.full_name || '',
      slug: item?.value?.link,
    })}`,
  };
});

useHead({
  title: itemTitle.value,
  titleTemplate: '%s', // Переопределяем titleTemplate для конкретной страницы
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items',
    },
  ],
  link: () => [
    ...alternateLinks.value,
    {
      href: getImageFromWaxpeer(item.value?.market_hash_name || '', '500:500'),
      rel: 'preload',
      as: 'image',
      fetchpriority: 'high',
    },
  ],
});

useSeoMeta({
  title: () => itemTitle.value,
  ogTitle: () => itemTitle.value,
  description: () => itemDescription.value,
  ogDescription: () => itemDescription.value,
  // ogImage: () => itemIcon.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => itemTitle.value,
  twitterDescription: () => itemDescription.value,
  // twitterImage: () => itemIcon.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  await defineUseProduct(item.value as ItemPageInfo),

  defineBreadcrumb({
    itemListElement: [
      { name: () => t('main'), item: '/' },
      {
        name: getWeaponOnly(item.value?.market_hash_name || ''),
        item: getWeaponLink(getWeaponOnly(item.value?.market_hash_name || '')),
      },
      {
        name: itemTitle.value,
        item: getItemLink({
          name: item.value?.market_hash_name || '',
          slug: item.value?.link,
        }),
      },
    ],
  }),
]);
</script>

<style scoped>

</style>
